import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError, fmtINR, API } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { Plus, Trash, ArrowLeft, DownloadSimple, Eye, PencilSimple, UploadSimple } from "@phosphor-icons/react";
import { SearchableSelect } from "../components/SearchableSelect";
import { VoucherDetailModal } from "../components/VoucherDetailModal";
import { PdfPreviewModal } from "../components/PdfPreviewModal";
import { LedgerFormModal } from "../components/LedgerFormModal";
import { LedgerImportModal } from "../components/LedgerImportModal";

const VTYPE_KEYS = { F4: "contra", F5: "payment", F6: "receipt", F7: "journal", F8: "sales", F9: "purchase" };
const TAB_KEYS = { d: "Day Book", b: "Cash/Bank Book", l: "Ledgers", t: "Trial Balance", p: "P&L", s: "Balance Sheet", g: "Groups", v: "New Voucher" };

const TABS = ["Day Book", "Cash/Bank Book", "Ledgers", "Trial Balance", "P&L", "Balance Sheet", "Groups", "New Voucher"];
const fyStart = () => { const d = new Date(); const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; return `${y}-04-01`; };
const today = () => new Date().toISOString().slice(0, 10);

export default function AccountingPage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(TABS.includes(params.get("tab")) ? params.get("tab") : "Day Book");
  const [quickVtype, setQuickVtype] = useState(null);
  useEffect(() => { const t = params.get("tab"); if (TABS.includes(t)) setTab(t); }, [params]);

  useEffect(() => {
    const handler = (e) => {
      const t = VTYPE_KEYS[e.key];
      if (t && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setQuickVtype(t);
        setTab("New Voucher");
        toast.info(`${e.key} → New ${t.charAt(0).toUpperCase() + t.slice(1)} Voucher`);
        return;
      }
      const tabName = TAB_KEYS[e.key.toLowerCase()];
      if (tabName && e.altKey && !e.ctrlKey && !e.metaKey) {
        const el = e.target;
        if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA") && el.dataset?.testid?.includes("search")) return;
        e.preventDefault();
        setTab(tabName);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  return (
    <div data-testid="accounting-page">
      <PageHeader title="Accounting (Tally-style)" subtitle="Ledgers, groups, vouchers, Day Book and Trial Balance — auto-posted from invoices, purchases and payments." />
      <div className="p-8 space-y-6">
        <div className="flex gap-1 flex-wrap">
          {TABS.map(t => {
            const key = Object.keys(TAB_KEYS).find(k => TAB_KEYS[k] === t);
            return (
              <button key={t} onClick={() => setTab(t)} title={`Alt+${key?.toUpperCase()}`} data-testid={`acc-tab-${t.toLowerCase().replace(/[&/ ]+/g, "-")}`}
                className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${tab === t ? "bg-brand text-white" : "bg-surface-muted text-ink-muted hover:bg-surface-hover"}`}>
                {t} <span className={`text-[9px] font-mono ml-1 ${tab === t ? "text-white/70" : "text-ink-muted/70"}`}>Alt+{key?.toUpperCase()}</span>
              </button>
            );
          })}
        </div>
        {tab === "Day Book" && <DayBook />}
        {tab === "Cash/Bank Book" && <CashBankBook />}
        {tab === "Ledgers" && <Ledgers />}
        {tab === "Trial Balance" && <TrialBalance />}
        {tab === "P&L" && <PnL />}
        {tab === "Balance Sheet" && <BalanceSheet />}
        {tab === "Groups" && <Groups />}
        {tab === "New Voucher" && <NewVoucher initialVtype={quickVtype} />}
      </div>
      <FieldStyle />
    </div>
  );
}

function DayBook() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const closeModal = React.useCallback(() => setOpenId(null), []);
  const load = async () => {
    try { const { data } = await api.get("/accounting/daybook", { params: { from, to }, timeout: 60000 }); setData(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-4">
      <VoucherDetailModal voucherId={openId} onClose={closeModal} />
      <div className="card-base p-4 flex items-end gap-3 flex-wrap">
        <div><div className="label-overline mb-1">From</div><input type="date" className="field" value={from} onChange={e => setFrom(e.target.value)} data-testid="db-from" /></div>
        <div><div className="label-overline mb-1">To</div><input type="date" className="field" value={to} onChange={e => setTo(e.target.value)} data-testid="db-to" /></div>
        <button onClick={load} className="btn-primary" data-testid="db-run">Show Day Book</button>
        <button onClick={async () => {
          try {
            const res = await fetch(`${API}/tally/export.xml?from=${from}&to=${to}`, { credentials: "include" });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `tally-vouchers-${from}-to-${to}.xml`; a.click();
            URL.revokeObjectURL(url);
            toast.success("Tally XML downloaded — import via Gateway of Tally → Import Data → Vouchers");
          } catch { toast.error("Export failed"); }
        }} className="btn-secondary inline-flex items-center gap-1.5" data-testid="db-tally-export"><DownloadSimple size={14} weight="bold" /> Export to Tally (XML)</button>
      </div>
      {data && (
        <div className="table-wrapper">
          <table className="table-base">
            <thead><tr><th>Date</th><th>Vch No</th><th>Ref</th><th>Type</th><th>Particulars</th><th className="text-right">Amount</th><th></th></tr></thead>
            <tbody>
              {data.rows.map(v => (
                <tr key={v.id} data-testid={`db-row-${v.id}`} className="cursor-pointer hover:bg-surface-hover focus:bg-surface-hover outline-none" tabIndex={0}
                  onClick={() => setOpenId(v.id)} onKeyDown={e => { if (e.key === "Enter") setOpenId(v.id); }} title="Click / Enter to view voucher">
                  <td className="tabular text-xs">{v.date}</td>
                  <td className="font-mono text-xs font-bold">{v.voucher_no || "—"}</td>
                  <td className="font-mono text-xs">{v.number || "—"}</td>
                  <td><span className="status-pill status-neutral">{v.voucher_type}</span>{v.source !== "system" && <span className="ml-1 status-pill status-info">{v.source === "manual" ? "manual" : "tally"}</span>}</td>
                  <td className="text-xs">{v.narration}<div className="text-[10px] text-ink-muted">{v.lines.map(l => `${l.ledger_name} ${l.dr > 0 ? "Dr " + l.dr : "Cr " + l.cr}`).join(" · ")}</div></td>
                  <td className="text-right tabular font-semibold">{fmtINR(v.total)}</td>
                  <td className="text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setOpenId(v.id)} className="btn-ghost inline-flex items-center gap-1 text-xs" data-testid={`db-view-${v.id}`}><Eye size={13} /> View</button>
                    {v.source !== "system" && <button onClick={async () => { try { await api.delete(`/accounting/vouchers/${v.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } }} className="btn-ghost text-destructive"><Trash size={13} /></button>}
                  </td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-ink-muted">No vouchers in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CashBankBook() {
  const [ledgers, setLedgers] = useState([]);
  const [ledgerId, setLedgerId] = useState("");
  const [from, setFrom] = useState(fyStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [openDay, setOpenDay] = useState({});
  const [openId, setOpenId] = useState(null);
  const closeModal = React.useCallback(() => setOpenId(null), []);

  useEffect(() => {
    api.get("/accounting/ledgers").then(r => {
      const cashBank = r.data.filter(l => ["Bank Accounts", "Cash-in-Hand"].includes(l.group_name));
      setLedgers(cashBank.length ? cashBank : r.data);
      if (cashBank[0]) setLedgerId(cashBank[0].id);
    });
  }, []);
  const load = async (lid = ledgerId) => {
    if (!lid) return;
    try { const { data } = await api.get("/accounting/cashbook", { params: { ledger_id: lid, from, to }, timeout: 60000 }); setData(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { if (ledgerId) load(ledgerId); /* eslint-disable-next-line */ }, [ledgerId]);

  return (
    <div className="space-y-4" data-testid="cashbook-view">
      <VoucherDetailModal voucherId={openId} onClose={closeModal} />
      <div className="card-base p-4 flex items-end gap-3 flex-wrap">
        <div><div className="label-overline mb-1">Cash / Bank Ledger</div>
          <select className="field w-56" value={ledgerId} onChange={e => setLedgerId(e.target.value)} data-testid="cb-ledger">
            {ledgers.map(l => <option key={l.id} value={l.id}>{l.name} ({l.group_name})</option>)}
          </select></div>
        <div><div className="label-overline mb-1">From</div><input type="date" className="field" value={from} onChange={e => setFrom(e.target.value)} data-testid="cb-from" /></div>
        <div><div className="label-overline mb-1">To</div><input type="date" className="field" value={to} onChange={e => setTo(e.target.value)} data-testid="cb-to" /></div>
        <button onClick={() => load()} className="btn-primary" data-testid="cb-run">Show Book</button>
      </div>
      {data && (
        <>
          <ContraShortcut ledgers={ledgers} onDone={() => load()} />
          <div className="card-base p-4 flex items-center gap-6 flex-wrap text-sm" data-testid="cb-summary">
            <div><span className="text-ink-muted text-xs">Opening:</span> <b className="tabular">{fmtINR(data.opening.amount)} {data.opening.type}</b></div>
            <div><span className="text-ink-muted text-xs">Total Receipts (Dr):</span> <b className="tabular text-brand">{fmtINR(data.totals.dr)}</b></div>
            <div><span className="text-ink-muted text-xs">Total Payments (Cr):</span> <b className="tabular text-destructive">{fmtINR(data.totals.cr)}</b></div>
            <div><span className="text-ink-muted text-xs">Closing:</span> <b className="tabular">{fmtINR(data.closing.amount)} {data.closing.type}</b></div>
          </div>
          <div className="table-wrapper">
            <table className="table-base">
              <thead><tr><th></th><th>Date</th><th className="text-right">Receipts (Dr)</th><th className="text-right">Payments (Cr)</th><th className="text-right">Closing Balance</th></tr></thead>
              <tbody>
                {data.days.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-ink-muted">No transactions in this period.</td></tr>}
                {data.days.map((d, i) => (
                  <React.Fragment key={d.date}>
                    <tr className="cursor-pointer hover:bg-surface-hover" onClick={() => setOpenDay(o => ({ ...o, [d.date]: !o[d.date] }))} data-testid={`cb-day-${i}`}>
                      <td className="w-8 text-xs">{openDay[d.date] ? "▾" : "▸"}</td>
                      <td className="tabular font-semibold">{d.date}<span className="text-[10px] text-ink-muted ml-2">{d.entries.length} entr{d.entries.length === 1 ? "y" : "ies"}</span></td>
                      <td className="text-right tabular text-brand">{d.dr > 0 ? fmtINR(d.dr) : ""}</td>
                      <td className="text-right tabular text-destructive">{d.cr > 0 ? fmtINR(d.cr) : ""}</td>
                      <td className="text-right tabular font-bold">{fmtINR(d.balance)} {d.balance_type}</td>
                    </tr>
                    {openDay[d.date] && d.entries.map((e, j) => (
                      <tr key={`${e.voucher_id}-${j}`} className="bg-surface-muted/50 cursor-pointer hover:bg-surface-hover" onClick={() => setOpenId(e.voucher_id)} data-testid={`cb-entry-${e.voucher_id}`} title="Click to view voucher">
                        <td><Eye size={12} className="text-ink-muted" /></td>
                        <td className="text-xs pl-6"><span className="font-mono font-bold">#{e.voucher_no}</span> <span className="status-pill status-neutral ml-1">{e.voucher_type}</span> {e.particulars}<div className="text-[10px] text-ink-muted">{e.narration}</div></td>
                        <td className="text-right tabular text-xs">{e.dr > 0 ? fmtINR(e.dr) : ""}</td>
                        <td className="text-right tabular text-xs">{e.cr > 0 ? fmtINR(e.cr) : ""}</td>
                        <td></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {data.days.length > 0 && (
                  <tr className="bg-surface-muted font-bold" data-testid="cb-total-row">
                    <td></td><td>Total</td>
                    <td className="text-right tabular">{fmtINR(data.totals.dr)}</td>
                    <td className="text-right tabular">{fmtINR(data.totals.cr)}</td>
                    <td className="text-right tabular">{fmtINR(data.closing.amount)} {data.closing.type}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ContraShortcut({ ledgers, onDone }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(today());
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!from || !to || from === to || !(Number(amount) > 0)) { toast.error("Pick two different ledgers and an amount"); return; }
    setSaving(true);
    try {
      const fromName = ledgers.find(l => l.id === from)?.name;
      const toName = ledgers.find(l => l.id === to)?.name;
      const { data } = await api.post("/accounting/vouchers", {
        voucher_type: "contra", date, narration: `Contra: ${fromName} → ${toName}`,
        lines: [{ ledger_id: to, dr: Number(amount), cr: 0 }, { ledger_id: from, dr: 0, cr: Number(amount) }],
      });
      toast.success(`Contra voucher ${data.number} posted`);
      setAmount("");
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-base p-4 flex items-end gap-3 flex-wrap border-dashed" data-testid="contra-card">
      <div className="label-overline w-full">Quick Contra Entry (cash ↔ bank transfer)</div>
      <div><div className="text-[10px] text-ink-muted mb-1">From (credit)</div>
        <select className="field w-44" value={from} onChange={e => setFrom(e.target.value)} data-testid="contra-from">
          <option value="">Select…</option>
          {ledgers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select></div>
      <div><div className="text-[10px] text-ink-muted mb-1">To (debit)</div>
        <select className="field w-44" value={to} onChange={e => setTo(e.target.value)} data-testid="contra-to">
          <option value="">Select…</option>
          {ledgers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select></div>
      <div><div className="text-[10px] text-ink-muted mb-1">Amount (₹)</div>
        <input type="number" step="0.01" className="field w-32 text-right" value={amount} onChange={e => setAmount(e.target.value)} data-testid="contra-amount" /></div>
      <div><div className="text-[10px] text-ink-muted mb-1">Date</div>
        <input type="date" className="field" value={date} onChange={e => setDate(e.target.value)} data-testid="contra-date" /></div>
      <button onClick={save} disabled={saving} className="btn-primary" data-testid="contra-save">{saving ? "Posting…" : "Post Contra"}</button>
    </div>
  );
}

function Ledgers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [stmt, setStmt] = useState(null);
  const [openId, setOpenId] = useState(null);
  const closeModal = React.useCallback(() => setOpenId(null), []);
  const [pdf, setPdf] = useState(null);
  const closePdf = React.useCallback(() => setPdf(null), []);
  const load = async () => { const { data } = await api.get("/accounting/ledgers", { params: { q: q || undefined } }); setRows(data); };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  const [ledgerModal, setLedgerModal] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [filt, setFilt] = useState({ from: fyStart(), to: today(), vtype: "" });
  const open = async (id, f = filt) => {
    try { const { data } = await api.get(`/accounting/ledger/${id}`, { params: { from: f.from, to: f.to, vtype: f.vtype || undefined }, timeout: 60000 }); setStmt(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const lid = params.get("ledger");
    const grp = params.get("group");
    if (grp) { setQ(grp); setStmt(null); setParams({ tab: "Ledgers" }, { replace: true }); return; }
    if (!lid) return;
    open(lid);
    setParams({ tab: "Ledgers" }, { replace: true });
    // eslint-disable-next-line
  }, [params]);
  const pdfQs = `from=${filt.from}&to=${filt.to}${filt.vtype ? `&vtype=${filt.vtype}` : ""}`;
  if (stmt) return (
    <div className="space-y-4" data-testid="ledger-statement">
      <VoucherDetailModal voucherId={openId} onClose={closeModal} />
      {pdf && <PdfPreviewModal src={pdf.src} filename={pdf.filename} title={pdf.title} onClose={closePdf} />}
      <div className="flex items-center justify-between">
        <button onClick={() => setStmt(null)} className="btn-secondary inline-flex items-center gap-1 text-xs"><ArrowLeft size={13} /> All Ledgers</button>
        <button onClick={() => setPdf({ src: `${API}/accounting/ledger/${stmt.ledger.id}/statement.pdf?${pdfQs}`, filename: `ledger-${stmt.ledger.name.replace(/[^A-Za-z0-9]+/g, "-")}.pdf`, title: `Ledger Statement · ${stmt.ledger.name}` })}
          className="btn-primary inline-flex items-center gap-1.5 text-xs" data-testid="ledger-pdf-btn"><DownloadSimple size={13} weight="bold" /> PDF Statement (Letterhead)</button>
      </div>
      <div className="card-base p-4">
        <div className="font-display text-lg font-semibold">{stmt.ledger.name}</div>
        <div className="text-xs text-ink-muted">{stmt.ledger.group_name} · Opening {fmtINR(stmt.opening.amount)} {stmt.opening.type} · Closing <b>{fmtINR(stmt.closing.amount)} {stmt.closing.type}</b></div>
      </div>
      <div className="card-base p-4 flex items-end gap-3 flex-wrap" data-testid="ls-filters">
        <div><div className="label-overline mb-1">From</div><input type="date" className="field" value={filt.from} onChange={e => setFilt({ ...filt, from: e.target.value })} data-testid="ls-from" /></div>
        <div><div className="label-overline mb-1">To</div><input type="date" className="field" value={filt.to} onChange={e => setFilt({ ...filt, to: e.target.value })} data-testid="ls-to" /></div>
        <div><div className="label-overline mb-1">Voucher Type</div>
          <select className="field w-44" value={filt.vtype} onChange={e => setFilt({ ...filt, vtype: e.target.value })} data-testid="ls-vtype">
            <option value="">All types</option>
            {[...new Set([...(stmt.voucher_types || []), ...(filt.vtype ? [filt.vtype] : [])])].map(t => <option key={t} value={t}>{t.replace("_", " ").replace(/^\w/, c => c.toUpperCase())}</option>)}
          </select></div>
        <button onClick={() => open(stmt.ledger.id)} className="btn-primary" data-testid="ls-apply">Apply</button>
        {(filt.vtype || filt.from !== fyStart() || filt.to !== today()) && <button onClick={() => { const f = { from: fyStart(), to: today(), vtype: "" }; setFilt(f); open(stmt.ledger.id, f); }} className="btn-secondary" data-testid="ls-reset">Reset</button>}
        <div className="ml-auto text-xs text-ink-muted self-center" data-testid="ls-summary">{stmt.totals?.count ?? stmt.entries.length} entr{(stmt.totals?.count ?? stmt.entries.length) === 1 ? "y" : "ies"}{stmt.vtype && <> · <span className="status-pill status-info">{stmt.vtype}</span> only</>}</div>
      </div>
      <div className="table-wrapper">
        <table className="table-base">
          <thead><tr><th>Date</th><th>Voucher</th><th>Particulars</th><th className="text-right">Debit</th><th className="text-right">Credit</th><th className="text-right">Running Dr / Cr</th><th className="text-right">Balance</th></tr></thead>
          <tbody>
            {stmt.entries.map((e, i) => (
              <tr key={`${e.voucher_id}-${i}`} className="cursor-pointer hover:bg-surface-hover" onClick={() => setOpenId(e.voucher_id)} data-testid={`ls-entry-${e.voucher_id}`} title="Click to view voucher"><td className="tabular text-xs">{e.date}</td><td className="font-mono text-xs"><span className="font-bold">#{e.voucher_no || "—"}</span> <span className="text-ink-muted">{e.number || ""}</span><div><span className="status-pill status-neutral">{e.voucher_type}</span></div></td><td className="text-xs">{e.narration}</td>
                <td className="text-right tabular">{e.dr > 0 ? fmtINR(e.dr) : ""}</td><td className="text-right tabular">{e.cr > 0 ? fmtINR(e.cr) : ""}</td>
                <td className="text-right tabular text-xs text-ink-muted">{fmtINR(e.run_dr)} / {fmtINR(e.run_cr)}</td>
                <td className="text-right tabular font-semibold">{fmtINR(e.balance)} {e.balance_type}</td></tr>
            ))}
            {stmt.entries.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-ink-muted">No entries for this filter.</td></tr>}
            {stmt.entries.length > 0 && (
              <tr className="bg-surface-muted font-bold" data-testid="ls-total-row">
                <td colSpan={3}>Total ({stmt.entries.length} entries)</td>
                <td className="text-right tabular">{fmtINR(stmt.totals.dr)}</td><td className="text-right tabular">{fmtINR(stmt.totals.cr)}</td>
                <td className="text-right tabular text-xs">Net {fmtINR(Math.abs(stmt.totals.dr - stmt.totals.cr))} {stmt.totals.dr >= stmt.totals.cr ? "Dr" : "Cr"}</td>
                <td className="text-right tabular">{fmtINR(stmt.closing.amount)} {stmt.closing.type}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
  const exportLedgers = async (format) => {
    try {
      const res = await fetch(`${API}/accounting/ledgers/export?format=${format}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const { downloadBlob } = await import("../lib/pdfShare");
      downloadBlob(await res.blob(), `ledger-master.${format}`);
      toast.success(format === "xml" ? "Tally XML downloaded — import it via Gateway of Tally → Import → Masters" : "Excel downloaded");
    } catch (e) { toast.error(e.message); }
  };
  const removeLedger = async (l) => {
    if (!window.confirm(`Delete ledger “${l.name}”? Only possible when it has no vouchers.`)) return;
    try { await api.delete(`/accounting/ledgers/${l.id}`); toast.success("Ledger deleted"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4">
      {ledgerModal && <LedgerFormModal initial={ledgerModal.id ? ledgerModal : undefined} onClose={() => setLedgerModal(null)} onSaved={() => { setLedgerModal(null); load(); }} />}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <input className="field w-72" placeholder="Search ledgers…" value={q} onChange={e => setQ(e.target.value)} data-testid="ledger-search" />
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => exportLedgers("xlsx")} className="btn-secondary inline-flex items-center gap-1.5" data-testid="export-ledgers-xlsx"><DownloadSimple size={14} /> Excel</button>
          <button onClick={() => exportLedgers("xml")} className="btn-secondary inline-flex items-center gap-1.5" data-testid="export-ledgers-xml"><DownloadSimple size={14} /> Tally XML</button>
          <button onClick={() => setImportOpen(true)} className="btn-secondary inline-flex items-center gap-1.5" data-testid="import-ledgers-btn"><UploadSimple size={14} /> Import (Tally XML / Excel)</button>
          <button onClick={() => setLedgerModal({})} className="btn-primary inline-flex items-center gap-1.5" data-testid="new-ledger-btn"><Plus size={14} weight="bold" /> New Ledger</button>
        </div>
      </div>
      {importOpen && <LedgerImportModal onClose={() => setImportOpen(false)} onDone={load} />}
      <div className="table-wrapper">
        <table className="table-base">
          <thead><tr><th>Ledger</th><th>Group</th><th className="text-right">Opening</th><th></th></tr></thead>
          <tbody>
            {rows.map(l => (
              <tr key={l.id} data-testid={`ledger-row-${l.id}`}>
                <td className="font-semibold">{l.name}</td><td className="text-xs">{l.group_name}</td>
                <td className="text-right tabular">{fmtINR(l.opening_balance)} {l.opening_type?.toUpperCase()}</td>
                <td className="text-right whitespace-nowrap">
                  <button onClick={() => open(l.id)} className="btn-secondary text-xs" data-testid={`open-ledger-${l.id}`}>View Statement</button>
                  <button onClick={() => setLedgerModal({ id: l.id, name: l.name, group_id: l.group_id, opening_balance: l.opening_balance, opening_type: l.opening_type || "dr" })} className="btn-ghost text-xs" title="Edit" data-testid={`edit-ledger-${l.id}`}><PencilSimple size={13} /></button>
                  <button onClick={() => removeLedger(l)} className="btn-ghost text-xs text-destructive" title="Delete" data-testid={`delete-ledger-${l.id}`}><Trash size={13} /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-ink-muted">No ledgers match. Click “New Ledger” to add one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrialBalance() {
  const [asOn, setAsOn] = useState(today());
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/accounting/trial-balance", { params: { as_on: asOn }, timeout: 60000 }); setData(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-4">
      <div className="card-base p-4 flex items-end gap-3">
        <div><div className="label-overline mb-1">As on</div><input type="date" className="field" value={asOn} onChange={e => setAsOn(e.target.value)} data-testid="tb-ason" /></div>
        <button onClick={load} className="btn-primary" data-testid="tb-run">Show Trial Balance</button>
      </div>
      {data && (
        <div className="table-wrapper">
          <table className="table-base">
            <thead><tr><th>Ledger</th><th>Group</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.group}-${r.ledger}`}><td className="font-semibold">{r.ledger}</td><td className="text-xs">{r.group}</td>
                  <td className="text-right tabular">{r.dr > 0 ? fmtINR(r.dr) : ""}</td><td className="text-right tabular">{r.cr > 0 ? fmtINR(r.cr) : ""}</td></tr>
              ))}
              <tr className="bg-surface-muted font-bold" data-testid="tb-total-row">
                <td colSpan={2}>Total</td><td className="text-right tabular">{fmtINR(data.total_dr)}</td><td className="text-right tabular">{fmtINR(data.total_cr)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PnL() {
  const [from, setFrom] = useState(fyStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/accounting/pnl", { params: { from, to }, timeout: 60000 }); setData(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-4" data-testid="pnl-view">
      <div className="card-base p-4 flex items-end gap-3 flex-wrap">
        <div><div className="label-overline mb-1">From</div><input type="date" className="field" value={from} onChange={e => setFrom(e.target.value)} data-testid="pnl-from" /></div>
        <div><div className="label-overline mb-1">To</div><input type="date" className="field" value={to} onChange={e => setTo(e.target.value)} data-testid="pnl-to" /></div>
        <button onClick={load} className="btn-primary" data-testid="pnl-run">Show P&L</button>
      </div>
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SideTable title="Expenses (Dr)" rows={data.expense} total={data.total_expense} testId="pnl-expense" />
          <SideTable title="Income (Cr)" rows={data.income} total={data.total_income} testId="pnl-income" />
          <div className={`lg:col-span-2 card-base p-4 flex items-center justify-between ${data.net_profit >= 0 ? "bg-brand text-white" : "bg-destructive text-white"}`} data-testid="pnl-net">
            <div className="font-display font-semibold">{data.net_profit >= 0 ? "Net Profit" : "Net Loss"}</div>
            <div className="font-display text-xl font-bold tabular">{fmtINR(Math.abs(data.net_profit))}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function BalanceSheet() {
  const [asOn, setAsOn] = useState(today());
  const [data, setData] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/accounting/balance-sheet", { params: { as_on: asOn }, timeout: 60000 }); setData(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-4" data-testid="bs-view">
      <div className="card-base p-4 flex items-end gap-3">
        <div><div className="label-overline mb-1">As on</div><input type="date" className="field" value={asOn} onChange={e => setAsOn(e.target.value)} data-testid="bs-ason" /></div>
        <button onClick={load} className="btn-primary" data-testid="bs-run">Show Balance Sheet</button>
      </div>
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SideTable title={`Liabilities — ${fmtINR(data.total_liabilities)}`} rows={[...data.liabilities, { ledger: data.net_profit >= 0 ? "Profit & Loss A/c (Profit)" : "Profit & Loss A/c (Loss)", group: "Capital Account", amount: data.net_profit }]} total={data.total_liabilities} testId="bs-liab" />
          <SideTable title={`Assets — ${fmtINR(data.total_assets)}`} rows={data.assets} total={data.total_assets} testId="bs-assets" />
        </div>
      )}
    </div>
  );
}

function SideTable({ title, rows, total, testId }) {
  return (
    <div className="table-wrapper" data-testid={testId}>
      <table className="table-base">
        <thead><tr><th>{title}</th><th className="text-right">Amount</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.group}-${r.ledger}`}><td><span className="font-semibold">{r.ledger}</span><span className="text-[10px] text-ink-muted ml-2">{r.group}</span></td>
              <td className="text-right tabular">{fmtINR(r.amount)}</td></tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={2} className="text-center py-5 text-ink-muted">No entries.</td></tr>}
          <tr className="bg-surface-muted font-bold"><td>Total</td><td className="text-right tabular">{fmtINR(total)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

const NATURES = [["asset", "Asset"], ["liability", "Liability"], ["income", "Income"], ["expense", "Expense"]];

function Groups() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", nature: "expense" });
  const [edit, setEdit] = useState(null); // { id, name, nature }
  const [filter, setFilter] = useState("");
  const load = React.useCallback(async () => {
    try { const { data } = await api.get("/accounting/groups", { params: { with_counts: 1 }, timeout: 60000 }); setRows(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.post("/accounting/groups", form); toast.success("Group added"); setForm({ name: "", nature: "expense" }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try { await api.put(`/accounting/groups/${edit.id}`, { name: edit.name.trim(), nature: edit.nature }); toast.success("Group updated"); setEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Delete group “${g.name}”?`)) return;
    try { await api.delete(`/accounting/groups/${g.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const shown = rows.filter(g => !filter || g.name.toLowerCase().includes(filter.toLowerCase()) || g.nature === filter);
  const byNature = NATURES.map(([k, label]) => ({ k, label, n: rows.filter(g => g.nature === k).length, ledgers: rows.filter(g => g.nature === k).reduce((s, g) => s + (g.ledger_count || 0), 0) }));
  return (
    <div className="space-y-4" data-testid="groups-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {byNature.map(b => (
          <button key={b.k} onClick={() => setFilter(filter === b.k ? "" : b.k)} className={`card-base p-3 text-left transition-colors ${filter === b.k ? "ring-2 ring-brand" : "hover:bg-surface-hover"}`} data-testid={`grp-nature-card-${b.k}`}>
            <div className="label-overline">{b.label}</div>
            <div className="font-display text-xl font-bold">{b.n} <span className="text-xs font-sans font-normal text-ink-muted">group{b.n !== 1 ? "s" : ""}</span></div>
            <div className="text-xs text-ink-muted">{b.ledgers} ledger{b.ledgers !== 1 ? "s" : ""}</div>
          </button>
        ))}
      </div>
      <div className="card-base p-4 flex items-end gap-3 flex-wrap">
        <div><div className="label-overline mb-1">New Group Name</div><input className="field w-64" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} onKeyDown={e => { if (e.key === "Enter" && form.name) save(); }} data-testid="grp-name" /></div>
        <div><div className="label-overline mb-1">Nature</div>
          <select className="field" value={form.nature} onChange={e => setForm({ ...form, nature: e.target.value })} data-testid="grp-nature">
            {NATURES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></div>
        <button onClick={save} disabled={!form.name} className="btn-primary inline-flex items-center gap-1" data-testid="grp-add"><Plus size={13} /> Add Group</button>
        <input className="field w-56 ml-auto" placeholder="Filter groups…" value={NATURES.some(([k]) => k === filter) ? "" : filter} onChange={e => setFilter(e.target.value)} data-testid="grp-filter" />
      </div>
      <div className="table-wrapper">
        <table className="table-base">
          <thead><tr><th>Group</th><th>Nature</th><th className="text-right">Ledgers</th><th className="text-right">Net Balance</th><th></th></tr></thead>
          <tbody>
            {shown.map(g => (
              <tr key={g.id} data-testid={`grp-row-${g.id}`}>
                {edit?.id === g.id ? (
                  <>
                    <td><input className="field" value={edit.name} onChange={e => setEdit({ ...edit, name: e.target.value })} onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEdit(null); }} autoFocus data-testid="grp-edit-name" /></td>
                    <td><select className="field" value={edit.nature} onChange={e => setEdit({ ...edit, nature: e.target.value })} data-testid="grp-edit-nature">{NATURES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                  </>
                ) : (
                  <>
                    <td className="font-semibold">{g.name}{g.system && <span className="ml-2 status-pill status-neutral">system</span>}</td>
                    <td><span className={`status-pill ${g.nature === "asset" || g.nature === "expense" ? "status-info" : "status-success"}`}>{g.nature}</span></td>
                  </>
                )}
                <td className="text-right tabular">
                  {g.ledger_count > 0 ? <Link to={`/accounting?tab=Ledgers&group=${encodeURIComponent(g.name)}`} className="underline decoration-dotted hover:text-brand" data-testid={`grp-ledgers-${g.id}`}>{g.ledger_count}</Link> : <span className="text-ink-muted">0</span>}
                </td>
                <td className="text-right tabular">{g.balance ? <>{fmtINR(g.balance)} <span className="text-xs text-ink-muted">{g.balance_type}</span></> : <span className="text-ink-muted">—</span>}</td>
                <td className="text-right whitespace-nowrap">
                  {edit?.id === g.id ? (
                    <><button onClick={saveEdit} className="btn-primary text-xs" data-testid="grp-edit-save">Save</button><button onClick={() => setEdit(null)} className="btn-ghost text-xs">Cancel</button></>
                  ) : (
                    <><button onClick={() => setEdit({ id: g.id, name: g.name, nature: g.nature })} className="btn-ghost" title="Rename / change nature" data-testid={`grp-edit-${g.id}`}><PencilSimple size={13} /></button>
                      {!g.system && <button onClick={() => remove(g)} className="btn-ghost text-destructive" disabled={g.ledger_count > 0} title={g.ledger_count > 0 ? "Move its ledgers first" : "Delete"} data-testid={`grp-delete-${g.id}`}><Trash size={13} /></button>}</>
                  )}
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-ink-muted">No groups match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const newVLine = () => ({ _uid: Math.random().toString(36).slice(2), ledger_id: "", dr: 0, cr: 0 });

function NewVoucher({ initialVtype }) {
  const [ledgers, setLedgers] = useState([]);
  const [vtype, setVtype] = useState(initialVtype || "journal");
  const [date, setDate] = useState(today());
  const [narration, setNarration] = useState("");
  const [lines, setLines] = useState([newVLine(), newVLine()]);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [quickL, setQuickL] = useState(null); // {i, name, group_id}
  const [openId, setOpenId] = useState(null);
  const closeModal = React.useCallback(() => setOpenId(null), []);
  const createQuickLedger = async () => {
    if (!quickL?.name?.trim() || !quickL.group_id) { toast.error("Enter a name and pick a group"); return; }
    try {
      const { data } = await api.post("/accounting/ledgers", { name: quickL.name.trim(), group_id: quickL.group_id, opening_balance: 0, opening_type: "dr" });
      setLedgers(ls => [...ls, data]);
      setLine(quickL.i, { ledger_id: data.id });
      toast.success(`Ledger "${data.name}" created`);
      const i = quickL.i;
      setQuickL(null);
      setTimeout(() => document.querySelector(`[data-testid="vch-dr-${i}"]`)?.focus(), 80);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { api.get("/accounting/ledgers").then(r => setLedgers(r.data)); api.get("/accounting/groups").then(r => setGroups(r.data)); }, []);
  useEffect(() => { if (initialVtype) setVtype(initialVtype); }, [initialVtype]);
  const cardRef = React.useRef(null);
  // Tally-style Enter flow: Enter moves to the next field; from the last amount
  // field of a balanced voucher it jumps to Save; Enter on Save posts the voucher.
  const onEnterNav = (e) => {
    if (e.key !== "Enter") return;
    const t = e.target;
    if (t.tagName === "BUTTON" || t.closest("[data-testid$='-dropdown']")) return;
    e.preventDefault();
    const els = Array.from(cardRef.current.querySelectorAll(
      "select.field, input.field, [data-testid^='vch-ledger-'], [data-testid='vch-save']"
    )).filter(el => !el.disabled && el.offsetParent !== null && !el.closest("[data-testid$='-dropdown']"));
    const idx = els.indexOf(t);
    const balanced = Math.abs(tdr - tcr) <= 0.01 && tdr > 0;
    const isLastAmount = t === els[els.length - 2] || /vch-(dr|cr)-\d+$/.test(t.dataset?.testid || "") && idx === els.length - 2;
    if (balanced && (isLastAmount || idx >= els.length - 2)) {
      document.querySelector("[data-testid='vch-save']")?.focus();
      return;
    }
    els[idx + 1]?.focus();
  };
  const setLine = (i, patch) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const tdr = lines.reduce((s, l) => s + Number(l.dr || 0), 0);
  const tcr = lines.reduce((s, l) => s + Number(l.cr || 0), 0);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/accounting/vouchers", {
        voucher_type: vtype, date, narration,
        lines: lines.filter(l => l.ledger_id).map(l => ({ ledger_id: l.ledger_id, dr: Number(l.dr || 0), cr: Number(l.cr || 0) })),
      });
      toast.success(`Voucher ${data.number} saved`, { action: { label: "View", onClick: () => setOpenId(data.id) } });
      setLines([newVLine(), newVLine()]); setNarration("");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-base p-5 space-y-4 max-w-3xl" data-testid="new-voucher-card" ref={cardRef} onKeyDown={onEnterNav}>
      <VoucherDetailModal voucherId={openId} onClose={closeModal} />
      <div className="flex flex-wrap gap-1.5 text-[10px]" data-testid="vch-shortcuts">
        {Object.entries({ F4: "Contra", F5: "Payment", F6: "Receipt", F7: "Journal", F8: "Sales", F9: "Purchase" }).map(([k, lbl]) => (
          <button key={k} onClick={() => setVtype(lbl.toLowerCase())}
            className={`px-2 py-1 rounded border font-mono ${vtype === lbl.toLowerCase() ? "bg-brand text-white border-brand" : "border-border text-ink-muted hover:border-brand"}`}>
            <b>{k}</b> {lbl}
          </button>
        ))}
        <span className="ml-auto text-ink-muted self-center">F4–F9 voucher type · type to search ledger · <b>Enter</b> moves ahead & saves (Tally flow)</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><div className="label-overline mb-1">Voucher Type</div>
          <select className="field" value={vtype} onChange={e => setVtype(e.target.value)} data-testid="vch-type">
            {["journal", "receipt", "payment", "contra", "sales", "purchase"].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select></div>
        <div><div className="label-overline mb-1">Date</div><input type="date" className="field" value={date} onChange={e => setDate(e.target.value)} data-testid="vch-date" /></div>
        <div><div className="label-overline mb-1">Narration</div><input className="field" value={narration} onChange={e => setNarration(e.target.value)} data-testid="vch-narration" /></div>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase text-ink-muted"><th className="py-1">Ledger</th><th className="text-right">Debit (₹)</th><th className="text-right">Credit (₹)</th><th></th></tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={l._uid}>
              <td className="py-1 pr-2">
                <SearchableSelect
                  testId={`vch-ledger-${i}`}
                  nextFocusId={`vch-dr-${i}`}
                  placeholder="Type ledger name…"
                  onCreateNew={(term) => setQuickL({ i, name: term, group_id: (groups.find(g => g.name === "Sundry Debtors") || groups[0] || {}).id || "" })}
                  options={ledgers.map(ld => ({ value: ld.id, label: `${ld.name} (${ld.group_name})` }))}
                  value={l.ledger_id}
                  onChange={v => setLine(i, { ledger_id: v })}
                />
              </td>
              <td className="py-1 pr-2"><input type="number" step="0.01" className="field text-right" value={l.dr} onChange={e => setLine(i, { dr: e.target.value, cr: 0 })} data-testid={`vch-dr-${i}`} /></td>
              <td className="py-1 pr-2"><input type="number" step="0.01" className="field text-right" value={l.cr} onChange={e => setLine(i, { cr: e.target.value, dr: 0 })} data-testid={`vch-cr-${i}`} /></td>
              <td><button onClick={() => setLines(ls => ls.filter((_, idx) => idx !== i))} className="btn-ghost text-destructive"><Trash size={13} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => setLines(ls => [...ls, newVLine()])} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="vch-add-line"><Plus size={13} /> Add Line</button>
        <div className="text-sm tabular">Dr <b>{fmtINR(tdr)}</b> · Cr <b>{fmtINR(tcr)}</b> {Math.abs(tdr - tcr) > 0.01 && <span className="text-destructive font-semibold ml-2">Not balanced</span>}</div>
        <button onClick={save} disabled={saving || Math.abs(tdr - tcr) > 0.01 || tdr <= 0} className="btn-primary" data-testid="vch-save">{saving ? "Saving…" : "Save Voucher"}</button>
      </div>
      {quickL && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" data-testid="alt-c-modal">
          <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-sm space-y-3">
            <div className="font-display font-semibold">Create Ledger <span className="text-[10px] text-ink-muted font-normal">(Tally Alt+C)</span></div>
            <div>
              <div className="label-overline mb-1">Ledger Name</div>
              <input autoFocus className="field" value={quickL.name} onChange={e => setQuickL({ ...quickL, name: e.target.value })} data-testid="alt-c-name"
                onKeyDown={e => { e.stopPropagation(); if (e.key === "Enter") createQuickLedger(); if (e.key === "Escape") setQuickL(null); }} />
            </div>
            <div>
              <div className="label-overline mb-1">Under Group</div>
              <select className="field" value={quickL.group_id} onChange={e => setQuickL({ ...quickL, group_id: e.target.value })} data-testid="alt-c-group">
                {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({g.nature})</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setQuickL(null)} className="btn-secondary" data-testid="alt-c-cancel">Cancel</button>
              <button onClick={createQuickLedger} className="btn-primary" data-testid="alt-c-save">Create & Use</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
